import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import AssetNav from "../components/AssetNav";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { canOperationalApprove, useAuth } from "../lib/auth";
import RecoveryDialog from "../components/RecoveryDialog";

export default function AssetIncidents({ embedded = false }) {
  const { user } = useAuth();
  const canApprove = canOperationalApprove(user);
  const [incidents, setIncidents] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [f, setF] = useState({ kind: "missing", mode: "item", item_id: "", qty: 1, unit_id: "", room_id: "", description: "" });
  const [recoverInc, setRecoverInc] = useState(null);

  const load = async () => {
    const [inc, i, u, r] = await Promise.all([api.get("/assets/incidents"), api.get("/assets/items", { params: { active: true } }), api.get("/assets/units"), api.get("/rooms")]);
    setIncidents(inc.data); setItems(i.data); setUnits(u.data.filter(x => !["disposed"].includes(x.status))); setRooms(r.data);
  };
  useEffect(() => { load(); }, []);

  const report = async () => {
    try {
      await api.post("/assets/incidents", {
        kind: f.kind,
        item_id: f.mode === "item" ? f.item_id : null,
        qty: f.mode === "item" ? Number(f.qty) : 0,
        unit_id: f.mode === "unit" ? f.unit_id : null,
        room_id: f.room_id || null, description: f.description,
      });
      toast.success("Incident reported");
      setF({ kind: "missing", mode: "item", item_id: "", qty: 1, unit_id: "", room_id: "", description: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const act = async (inc, action) => {
    const reason = window.prompt(`Reason for ${action}:`);
    if (!reason) return;
    try {
      await api.post(`/assets/incidents/${inc.id}/${action}`, { reason });
      toast.success(`Incident ${action.replace("-", " ")}`); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submitRecover = async (payload) => {
    await api.post(`/assets/incidents/${recoverInc.id}/recover`, {
      reason: payload.recovery_reason, location_found: payload.location_found,
      destination: payload.destination, room_id: payload.room_id,
      proof_url: payload.proof_url, recovered_qty: payload.recovered_qty,
    });
    const total = recoverInc.qty || 1;
    toast.success(payload.recovered_qty < total ? "Partially recovered" : "Recovered"); load();
  };

  const addNote = async (inc) => {
    const note = window.prompt("Investigation note:");
    if (!note) return;
    try { await api.post(`/assets/incidents/${inc.id}/note`, { note }); toast.success("Note added"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const content = (
    <>
      <Card className="p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 items-end">
          <div><Label>Kind</Label><Select value={f.kind} onValueChange={v => setF({ ...f, kind: v })}><SelectTrigger className="h-11 mt-1" data-testid="incident-kind"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="missing">Missing</SelectItem><SelectItem value="damaged">Damaged</SelectItem></SelectContent></Select></div>
          <div><Label>Target</Label><Select value={f.mode} onValueChange={v => setF({ ...f, mode: v })}><SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="item">Item + Qty</SelectItem><SelectItem value="unit">Tagged Asset</SelectItem></SelectContent></Select></div>
          {f.mode === "item" ? (
            <>
              <div><Label>Item</Label><Select value={f.item_id} onValueChange={v => setF({ ...f, item_id: v })}><SelectTrigger className="h-11 mt-1" data-testid="incident-item"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{items.filter(i => i.tracking_mode !== "tagged").map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Qty</Label><Input type="number" min="1" value={f.qty} onChange={e => setF({ ...f, qty: e.target.value })} className="h-11 mt-1" data-testid="incident-qty" /></div>
            </>
          ) : (
            <div className="col-span-2"><Label>Asset</Label><Select value={f.unit_id} onValueChange={v => setF({ ...f, unit_id: v })}><SelectTrigger className="h-11 mt-1" data-testid="incident-unit"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.asset_id} · {u.item_name}</SelectItem>)}</SelectContent></Select></div>
          )}
          <div><Label>Room</Label><Select value={f.room_id} onValueChange={v => setF({ ...f, room_id: v })}><SelectTrigger className="h-11 mt-1"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>Room {r.number}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Description</Label><Input value={f.description} onChange={e => setF({ ...f, description: e.target.value })} className="h-11 mt-1" data-testid="incident-description" /></div>
          <div><Button onClick={report} className="h-11 w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="report-incident">Report</Button></div>
        </div>
        <div className="text-xs text-slate-400 mt-2">Damaged items move to Damaged Holding (still accountable). An operational approver can approve a stock-changing loss or recovery.</div>
      </Card>

      <Card className="p-0 overflow-x-auto">
        <table className="vr-table" data-testid="incidents-table">
          <thead><tr><th>Number</th><th>Kind</th><th>Asset / Item</th><th className="num">Qty</th><th>Status</th><th>Reported By</th><th>Reason</th><th>Actions</th></tr></thead>
          <tbody>
            {incidents.map(inc => (
              <tr key={inc.id}>
                <td className="font-mono text-xs">{inc.number}</td>
                <td><Badge className={inc.kind === "missing" ? "bg-red-100 border text-red-800" : "bg-amber-100 border text-amber-800"}>{inc.kind}</Badge></td>
                <td className="text-xs">{inc.asset_label || items.find(i => i.id === inc.item_id)?.name || "—"}</td>
                <td className="num">{inc.qty || 1}</td>
                <td><Badge className={inc.status === "open" ? "bg-blue-100 border text-blue-800" : inc.status === "recovered" ? "bg-emerald-100 border text-emerald-800" : "bg-slate-100 border text-slate-600"}>{inc.status.replace("_", " ")}</Badge></td>
                <td className="text-xs">{inc.reported_by_name}</td>
                <td className="text-xs">{inc.reason || "—"}</td>
                <td>
                  {inc.status === "open" && (
                    <div className="flex gap-1">
                      {canApprove && <Button size="sm" variant="outline" className="text-red-700" onClick={() => act(inc, "approve-loss")} data-testid={`approve-loss-${inc.number}`}>Approve Loss</Button>}
                      {canApprove && <Button size="sm" variant="outline" onClick={() => setRecoverInc(inc)} data-testid={`recover-${inc.number}`}>Recover</Button>}
                      <Button size="sm" variant="outline" onClick={() => addNote(inc)} data-testid={`note-${inc.number}`}>Add Note</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <RecoveryDialog
        open={!!recoverInc} onOpenChange={o => !o && setRecoverInc(null)}
        title={recoverInc ? `Recover · ${recoverInc.asset_label || items.find(i => i.id === recoverInc.item_id)?.name || recoverInc.number}` : ""}
        maxQty={recoverInc?.qty || 1}
        destinationOptions={[{ value: "A_STORE", label: "Store" }, { value: "A_ROOM", label: "A Room" }]}
        roomOption="A_ROOM" rooms={rooms}
        onSubmit={submitRecover}
      />
    </>
  );

  if (embedded) return content;
  return (
    <Layout>
      <div className="mb-4"><h1 className="section-title">Missing, Damaged &amp; Disposal</h1></div>
      <AssetNav />
      {content}
    </Layout>
  );
}
